/**
 * System Notifications Hook
 * React Query hook for fetching and managing user-facing system broadcast notifications
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  systemNotificationsService,
  SystemNotification,
} from '@/services/system-notifications.service';
import { useAuth } from '@/hooks/useAuth';

/**
 * Query keys for system notifications
 */
const systemNotificationsQueryKeys = {
  all: ['systemNotifications'] as const,
  active: ['systemNotifications', 'active'] as const,
};

/**
 * Hook return type
 */
interface UseSystemNotificationsReturn {
  notifications: SystemNotification[];
  undismissedNotifications: SystemNotification[];
  criticalNotifications: SystemNotification[];
  regularNotifications: SystemNotification[];
  unreadCount: number;
  isLoading: boolean;
  error: Error | null;
  dismiss: (id: number) => void;
  refetch: () => void;
}

/**
 * System Notifications Hook
 *
 * Fetches active system notifications for the current user.
 * Separates critical notifications (for banner display) from regular notifications (for bell/widget).
 * Auto-refetches every 5 minutes to catch new notifications.
 *
 * Critical notifications are:
 * - MAINTENANCE type notifications
 * - WARNING type notifications with ERROR severity
 *
 * @returns Hook state with notifications, mutations, and helper functions
 *
 * @example
 * ```tsx
 * const { notifications, criticalNotifications, unreadCount, dismiss } = useSystemNotifications();
 *
 * // Show critical notifications in banner
 * {criticalNotifications.map(n => (
 *   <Banner key={n.id} severity={n.severity}>
 *     {n.message}
 *     {n.isDismissible && (
 *       <button onClick={() => dismiss(n.id)}>Dismiss</button>
 *     )}
 *   </Banner>
 * ))}
 *
 * // Show regular notifications in dropdown
 * <NotificationBell count={unreadCount} />
 * ```
 */
export function useSystemNotifications(): UseSystemNotificationsReturn {
  const queryClient = useQueryClient();
  const { isAuthenticated } = useAuth();

  // Fetch active notifications (only when authenticated)
  const {
    data: notifications = [],
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: systemNotificationsQueryKeys.active,
    queryFn: systemNotificationsService.getActiveNotifications,
    enabled: isAuthenticated, // Wait for auth before fetching
    staleTime: 5 * 60 * 1000, // 5 minutes - refetch on window focus if stale
    retry: 1, // Only retry once on failure
    refetchOnWindowFocus: true, // Refetch when user returns to tab
  });

  // Dismiss mutation
  const dismissMutation = useMutation({
    mutationFn: systemNotificationsService.dismissNotification,
    onSuccess: () => {
      // Invalidate and refetch to remove dismissed notification
      queryClient.invalidateQueries({ queryKey: systemNotificationsQueryKeys.active });
    },
  });

  // All undismissed notifications (for banner and count)
  const undismissedNotifications = notifications.filter(n => !n.isDismissed);

  // Separate critical from regular (for banner - only undismissed)
  const criticalNotifications = undismissedNotifications.filter((notification) => {
    // MAINTENANCE notifications are always critical
    if (notification.type === 'MAINTENANCE') return true;
    // WARNING notifications with ERROR severity are critical
    if (notification.type === 'WARNING' && notification.severity === 'ERROR') return true;
    return false;
  });

  // Regular notifications include ALL (dismissed and not) for the widget
  const regularNotifications = notifications.filter((notification) => {
    // Everything that's not critical type
    if (notification.type === 'MAINTENANCE') return false;
    if (notification.type === 'WARNING' && notification.severity === 'ERROR') return false;
    return true;
  });

  // Unread count (only undismissed notifications)
  const unreadCount = undismissedNotifications.length;

  return {
    notifications,
    undismissedNotifications,
    criticalNotifications,
    regularNotifications,
    unreadCount,
    isLoading,
    error: error as Error | null,
    dismiss: (id: number) => dismissMutation.mutate(id),
    refetch,
  };
}

/**
 * Export query keys for use in other hooks/components
 */
export { systemNotificationsQueryKeys };
